import React, { FC, useEffect } from 'react'
import { useParams } from 'react-router'
import { Redirect, useLocation } from 'react-router-dom'
import { usePublicApiSession } from 'front-end/src/public-api/hooks/usePublicApiSession'
import { linkBuilder, staticLinks } from 'shared/linkBuilder'
import { Box } from 'front-end/src/components'
import { notification } from '../../../components/common/notifications/Notifications'
import Loader from '../../../components/common/loader/Loader'
import HeadTitle from '../../../components/common/head/HeadTitle'
import { useLoginPageQuery } from './LoginPage.codegen'
import { BillingStatusType } from 'front-end/src/graphql/public/PublicSchemaTypes'
import { OrganisationNotFoundError } from 'front-end/src/pages/auth/login/components/OrganisationNotFoundError'
import { OrganisationSuspendedError } from 'front-end/src/pages/auth/login/components/OrganisationSuspendedError'
import { t } from '@lingui/macro'
import { WithAuthStateContext } from './state/WithAuthStateContext'
import { useLoginContext } from './LoginContext'
import { observer } from 'mobx-react'

const CurrentView: React.FC = observer(() => {
  const { stepView: StepView } = useLoginContext()

  return <StepView />
})
CurrentView.displayName = 'CurrentView'

export const LOGIN_PAGE_ERROR_LS_KEY = 'looop:loginMountErrorNotification'

export const LoginPage: FC = () => {
  const { token } = useParams<{ token?: string }>()
  const { pathname } = useLocation()
  const isSelfSignupWithLink =
    !!token && linkBuilder.secretSelfSignup(token) === pathname
  const { data, loading } = useLoginPageQuery({
    fetchPolicy: 'cache-first'
  })
  const { session } = usePublicApiSession()

  useEffect(() => {
    const msg = localStorage.getItem(LOGIN_PAGE_ERROR_LS_KEY)
    if (msg) {
      notification.error(msg)
      localStorage.removeItem(LOGIN_PAGE_ERROR_LS_KEY)
    }
  }, [])

  const organisation = data?.organisation

  if (!loading && organisation === null) {
    return <OrganisationNotFoundError />
  }

  if (loading || !data || !organisation) {
    return <Loader />
  }

  if (organisation?.billing_status === BillingStatusType.SUSPENDED) {
    return <OrganisationSuspendedError />
  }

  if (session && pathname === '/login') {
    return <Redirect to={staticLinks.home} />
  }

  return (
    <WithAuthStateContext
      args={{
        selfSignupToken: isSelfSignupWithLink ? token : null,
        organisation: data.organisation
      }}
    >
      <Box className="Auth">
        <HeadTitle title={t`Log in`} />
        <Box className="Auth__container">
          <CurrentView />
        </Box>
      </Box>
    </WithAuthStateContext>
  )
}
